import { useState, useEffect, useCallback } from "react";
import { BadgeCheck, TrendingDown, PlusCircle, Copy, Download, Loader2 } from "lucide-react";
import dayjs from "dayjs";
import { Shimmer, AnalyticsCardsShimmer, TableShimmer } from "@/components/ui/shimmer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import TransactionFiltersPanel, {
  getDefaultFilters,
  type FilterValues,
} from "@/components/common/TransactionFiltersPanel";
import { useAuth } from "@/context/AuthContext";
import {
  getCreditBalance,
  getCreditAnalytics,
  getCreditTransactions,
  getCreditTransactionTypes,
  getAllCreditTransactions,
  type CreditAnalytics,
  type CreditTransactionRow,
  type CreditTransactionFilters,
} from "@/utils/credit-api";
import { exportCreditTransactionsToExcel } from "@/utils/export-excel";

function formatCredits(val: number): string {
  return val.toLocaleString("en-IN");
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatType(type: string): string {
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function ComparisonLabel({ current, previous, label }: { current: number; previous: number; label: string }) {
  if (previous === 0) return <span className="text-xs text-muted-foreground">{label}</span>;
  const diff = current - previous;
  const pct = ((diff / Math.abs(previous)) * 100).toFixed(1);
  const isUp = diff >= 0;
  return (
    <span className={isUp ? "text-xs text-green-600 dark:text-green-400" : "text-xs text-red-500 dark:text-red-400"}>
      {isUp ? "↑" : "↓"} {Math.abs(Number(pct))}% vs yesterday
    </span>
  );
}

export default function CreditPage() {
  const { userId, token } = useAuth();

  // Analytics state
  const [balance, setBalance] = useState<number | null>(null);
  const [analytics, setAnalytics] = useState<CreditAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  // Table state
  const [rows, setRows] = useState<CreditTransactionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState<FilterValues>(getDefaultFilters);
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);

  // Fetch analytics
  useEffect(() => {
    if (!userId || !token) return;
    setAnalyticsLoading(true);

    const todayEnd = dayjs().endOf("day").toISOString();
    const todayStart = dayjs().startOf("day").toISOString();
    const yesterdayStart = dayjs().subtract(1, "day").startOf("day").toISOString();

    Promise.all([
      getCreditBalance(userId, token),
      getCreditAnalytics(userId, todayStart, todayEnd, yesterdayStart, token),
    ])
      .then(([bal, ana]) => { setBalance(bal); setAnalytics(ana); })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load analytics"))
      .finally(() => setAnalyticsLoading(false));
  }, [userId, token]);

  // Fetch types
  useEffect(() => {
    if (!userId || !token) return;
    getCreditTransactionTypes(userId, token).then(setAvailableTypes).catch(() => {});
  }, [userId, token]);

  // Build filters
  const buildFilters = useCallback((): CreditTransactionFilters => {
    const f: CreditTransactionFilters = { userId: userId! };
    if (filters.selectedTypes.length > 0) f.types = filters.selectedTypes;
    if (filters.createdFrom) f.createdFrom = filters.createdFrom.startOf("day").toISOString();
    if (filters.createdTo) f.createdTo = filters.createdTo.endOf("day").toISOString();
    if (filters.search.trim()) f.search = filters.search.trim();
    return f;
  }, [userId, filters]);

  // Fetch table data
  const fetchData = useCallback(async () => {
    if (!userId || !token) return;
    setLoading(true);
    setError("");
    try {
      const result = await getCreditTransactions(buildFilters(), rowsPerPage, page * rowsPerPage, token);
      setRows(result.rows);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load credit transactions");
    } finally {
      setLoading(false);
    }
  }, [userId, token, page, rowsPerPage, buildFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(0); }, [filters]);

  const handleExport = async () => {
    if (!userId || !token) return;
    setExporting(true);
    try {
      const allRows = await getAllCreditTransactions(buildFilters(), token);
      exportCreditTransactionsToExcel(allRows, "credit-transactions");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const copyId = (id: string) => { navigator.clipboard.writeText(id); };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Credit</h1>
        <p className="text-muted-foreground">
          View your API credit balance, usage analytics, and transaction history.
        </p>
      </div>

      {error && <Alert variant="destructive" className="mb-6"><AlertDescription>{error}</AlertDescription></Alert>}

      {/* Analytics Cards */}
      <Shimmer loading={analyticsLoading} fallback={<AnalyticsCardsShimmer />}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
          <div>
            <Card className="h-full">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <BadgeCheck className="text-primary" />
                  <span className="text-sm text-muted-foreground font-medium">Available Credit</span>
                </div>
                <h1 className="text-3xl font-bold">{balance !== null ? formatCredits(balance) : "—"}</h1>
                <span className="text-xs text-muted-foreground">Total credit balance</span>
              </CardContent>
            </Card>
          </div>

          <div>
            <Card className="h-full">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingDown className="text-red-500" />
                  <span className="text-sm text-muted-foreground font-medium">Today's Credit Used</span>
                </div>
                {analytics ? (
                  <>
                    <h1 className="text-3xl font-bold text-red-500 dark:text-red-400">
                      {formatCredits(Math.abs(analytics.todaySpent))}
                    </h1>
                    <ComparisonLabel current={Math.abs(analytics.todaySpent)} previous={Math.abs(analytics.yesterdaySpent)} label="No usage yesterday" />
                  </>
                ) : (
                  <h1 className="text-3xl font-bold text-red-500 dark:text-red-400">—</h1>
                )}
              </CardContent>
            </Card>
          </div>

          <div>
            <Card className="h-full">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <PlusCircle className="text-green-600" />
                  <span className="text-sm text-muted-foreground font-medium">Today's Credit Added</span>
                </div>
                {analytics ? (
                  <>
                    <h1 className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {formatCredits(analytics.todayAdded)}
                    </h1>
                    <ComparisonLabel current={analytics.todayAdded} previous={analytics.yesterdayAdded} label="No additions yesterday" />
                  </>
                ) : (
                  <h1 className="text-3xl font-bold text-green-600 dark:text-green-400">—</h1>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </Shimmer>

      {/* Transaction History */}
      <Shimmer loading={loading} fallback={<TableShimmer cols={7} />}>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div>
                <h3 className="text-base font-semibold">Credit Transaction History</h3>
                <span className="text-sm text-muted-foreground">
                  {total.toLocaleString()} transactions total
                </span>
              </div>
            </div>

            <TransactionFiltersPanel
              filters={filters}
              onChange={setFilters}
              availableTypes={availableTypes}
              actions={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={exporting || total === 0}
                >
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  {exporting ? "Exporting..." : "Export Excel"}
                </Button>
              }
            />

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Transaction ID</TableHead>
                  <TableHead className="font-semibold">Purpose</TableHead>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold text-right">Credit</TableHead>
                  <TableHead className="font-semibold">From</TableHead>
                  <TableHead className="font-semibold">Created At</TableHead>
                  <TableHead className="font-semibold">Updated At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <span className="text-muted-foreground">No credit transactions found</span>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((txn) => (
                    <TableRow key={txn.id}>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground font-mono">
                            {txn.id.slice(0, 8)}...
                          </span>
                          <Button variant="ghost" size="icon-xs" onClick={() => copyId(txn.id)}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm max-w-[240px] truncate block" title={txn.purpose}>
                          {txn.purpose}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={txn.credit >= 0 ? "border-green-500 text-green-600 dark:text-green-400" : ""}>{formatType(txn.type)}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={txn.credit >= 0 ? "text-sm font-semibold text-green-600 dark:text-green-400" : "text-sm font-semibold text-red-500 dark:text-red-400"}>
                          {txn.credit >= 0 ? "+" : ""}{formatCredits(txn.credit)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">
                          {txn.from ?? "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">{formatDate(txn.created_at)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">{formatDate(txn.updated_at)}</span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <DataTablePagination
              count={total}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(p) => setPage(p)}
              onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setPage(0); }}
            />
          </CardContent>
        </Card>
      </Shimmer>
    </div>
  );
}
